/**
 * Tag Indicator Plugin for Lexical
 * Shows subtle visual indicators on annotated blocks and handles hover to reveal tag names.
 * Supports block, block-span, and inline-range annotations.
 * Only visible to authors in the editor.
 */

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { type TagDefinition } from '@shared/tags/tagRegistry'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import { ContentAST } from '../../../../transforms/ast'
import { Annotation, isBlockAnchor, isBlockSpanAnchor } from '../../../../types/annotations'
import { getAnnotatedBlockIds } from '../../../../utils/annotationUtils'

/** First tag color defined on this annotation, or null. */
function findAnnotationColor(annotation: Annotation, tagRegistry: Record<string, TagDefinition>): string | null {
  for (const tagId of annotation.tags) {
    const color = tagRegistry[tagId]?.color
    if (color) {
      return color
    }
  }
  return null
}

export interface TagOverlayPosition {
  top: number
  left: number | null // null means use 'right' instead
  right: number | null // null means use 'left' instead
}

export interface TagIndicatorPluginProps {
  /** Annotations for the chapter */
  annotations?: Annotation[]
  /** AST for block span calculations */
  ast?: ContentAST
  /** Whether the current user is an author */
  isAuthor?: boolean
  /** Text direction for positioning overlay on correct side */
  direction?: 'ltr' | 'rtl'
  /** Tag registry — used to look up each tag's `color` for the side bar tint. */
  tagRegistry?: Record<string, TagDefinition>
  /** Callback when a block is hovered - provides block ID and position for overlay */
  onBlockHover?: (blockId: string | null, position: TagOverlayPosition | null) => void
}

// CSS classes for different annotation types
const TAGGED_BLOCK_CLASS = 'lexical-tagged-block'
const TAGGED_BLOCK_CLASS_LTR = 'lexical-tagged-block-ltr'
const TAGGED_BLOCK_CLASS_RTL = 'lexical-tagged-block-rtl'
const SPAN_START_CLASS = 'lexical-span-start'
const SPAN_MIDDLE_CLASS = 'lexical-span-middle'
const SPAN_END_CLASS = 'lexical-span-end'

// Inject styles once
let stylesInjected = false
function injectStyles() {
  if (stylesInjected) {
    return
  }
  stylesInjected = true

  // The bar color comes from --tag-color, set inline per block to that block's
  // dominant tag color. We fall back to Material Blue 500 when no color is set
  // (e.g. tags without a color field). `color-mix` lets one variable drive all
  // four visual variants (bar/hover bar/block tint/dashed outline) at different
  // alphas without computing them in JS.
  const style = document.createElement('style')
  style.textContent = `
    .${TAGGED_BLOCK_CLASS} {
      position: relative;
    }
    /* Side border indicator using pseudo-element - doesn't affect text position */
    .${TAGGED_BLOCK_CLASS}::before {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      width: 3px;
      background-color: color-mix(in srgb, var(--tag-color, #2196F3) 55%, transparent);
      border-radius: 2px;
      transition: background-color 0.15s ease;
    }
    .${TAGGED_BLOCK_CLASS_LTR}::before {
      left: -12px;
    }
    .${TAGGED_BLOCK_CLASS_RTL}::before {
      right: -12px;
    }
    /* Hover state: pale tinted background with dashed border, keep side border */
    .${TAGGED_BLOCK_CLASS}:hover {
      background-color: color-mix(in srgb, var(--tag-color, #2196F3) 8%, transparent);
      outline: 1px dashed color-mix(in srgb, var(--tag-color, #2196F3) 40%, transparent);
      outline-offset: 2px;
      border-radius: 4px;
    }
    .${TAGGED_BLOCK_CLASS}:hover::before {
      background-color: color-mix(in srgb, var(--tag-color, #2196F3) 75%, transparent);
    }

    /* Block span - hide pseudo-element, use real element instead */
    .${SPAN_START_CLASS}::before,
    .${SPAN_MIDDLE_CLASS}::before,
    .${SPAN_END_CLASS}::before {
      display: none !important;
    }

    /* Span blocks - no individual hover effects */
    .${SPAN_START_CLASS},
    .${SPAN_MIDDLE_CLASS},
    .${SPAN_END_CLASS} {
      margin-left: 0;
      margin-right: 0;
    }
    .${SPAN_START_CLASS}:hover,
    .${SPAN_MIDDLE_CLASS}:hover,
    .${SPAN_END_CLASS}:hover {
      background-color: transparent !important;
      outline: none !important;
    }

    /* Real single side indicator element for spans */
    .lexical-span-side-indicator {
      position: absolute;
      width: 3px;
      background-color: color-mix(in srgb, var(--tag-color, #2196F3) 55%, transparent);
      border-radius: 2px;
      pointer-events: none;
      z-index: 1;
      transition: background-color 0.15s ease;
    }
    .lexical-span-side-indicator.hovered {
      background-color: color-mix(in srgb, var(--tag-color, #2196F3) 75%, transparent);
    }
    /* The actual single overlay div for span hover */
    .lexical-span-overlay {
      position: absolute;
      background-color: color-mix(in srgb, var(--tag-color, #2196F3) 8%, transparent);
      border: 1px dashed color-mix(in srgb, var(--tag-color, #2196F3) 40%, transparent);
      border-radius: 4px;
      pointer-events: none;
      z-index: 0;
    }
  `
  document.head.appendChild(style)
}

/**
 * Build a map of block IDs to their span position (start, middle, end)
 */
function buildSpanPositionMap(
  annotations: Annotation[],
  ast: ContentAST,
): Map<string, 'start' | 'middle' | 'end' | 'single'> {
  const positionMap = new Map<string, 'start' | 'middle' | 'end' | 'single'>()

  for (const annotation of annotations) {
    if (isBlockSpanAnchor(annotation.anchor)) {
      const { startBlockId, endBlockId } = annotation.anchor
      const startIndex = ast.blocks.findIndex((b) => b.id === startBlockId)
      const endIndex = ast.blocks.findIndex((b) => b.id === endBlockId)

      if (startIndex === -1 || endIndex === -1) {
        continue
      }

      for (let i = startIndex; i <= endIndex; i++) {
        const blockId = ast.blocks[i].id
        if (i === startIndex && i === endIndex) {
          positionMap.set(blockId, 'single')
        } else if (i === startIndex) {
          positionMap.set(blockId, 'start')
        } else if (i === endIndex) {
          positionMap.set(blockId, 'end')
        } else {
          positionMap.set(blockId, 'middle')
        }
      }
    }
  }

  return positionMap
}

export function TagIndicatorPlugin({
  annotations = [],
  ast,
  isAuthor = false,
  direction = 'ltr',
  tagRegistry = {},
  onBlockHover,
}: TagIndicatorPluginProps) {
  const [editor] = useLexicalComposerContext()
  const hoveredBlockIdRef = useRef<string | null>(null)

  // Compute set of annotated block IDs
  const annotatedBlockIds = useMemo(() => {
    if (!ast) {
      return new Set<string>()
    }
    // Exclude blocks that ONLY have inline annotations - those are handled by InlineAnnotationPlugin
    return getAnnotatedBlockIds(annotations, ast, true)
  }, [annotations, ast])

  // Compute span position map for visual styling
  const spanPositionMap = useMemo(() => {
    if (!ast) {
      return new Map<string, 'start' | 'middle' | 'end' | 'single'>()
    }
    return buildSpanPositionMap(annotations, ast)
  }, [annotations, ast])

  // blockId → hex color for the side bar tint. First annotation on each block that
  // carries a colored tag wins; multiple-annotation overlaps fall back to the first
  // seen. Used to drive the `--tag-color` CSS variable on each block element.
  const blockTagColors = useMemo(() => {
    const colors = new Map<string, string>()
    if (!ast) {
      return colors
    }
    for (const annotation of annotations) {
      const color = findAnnotationColor(annotation, tagRegistry)
      if (!color) {
        continue
      }
      const { anchor } = annotation
      if (isBlockAnchor(anchor)) {
        if (!colors.has(anchor.blockId)) {
          colors.set(anchor.blockId, color)
        }
      } else if (isBlockSpanAnchor(anchor)) {
        const startIndex = ast.blocks.findIndex((b) => b.id === anchor.startBlockId)
        const endIndex = ast.blocks.findIndex((b) => b.id === anchor.endBlockId)
        for (let i = startIndex; i <= endIndex && i < ast.blocks.length; i++) {
          if (i >= 0 && !colors.has(ast.blocks[i].id)) {
            colors.set(ast.blocks[i].id, color)
          }
        }
      }
    }
    return colors
  }, [annotations, ast, tagRegistry])

  // Apply/remove tagged class + --tag-color to DOM elements
  const updateTaggedElements = useCallback(() => {
    if (!isAuthor) {
      return
    }

    const rootElement = editor.getRootElement()
    if (!rootElement) {
      return
    }

    const blockElements = rootElement.querySelectorAll('[data-block-id]')
    const dirClass = direction === 'rtl' ? TAGGED_BLOCK_CLASS_RTL : TAGGED_BLOCK_CLASS_LTR

    blockElements.forEach((element) => {
      const blockId = element.getAttribute('data-block-id')
      if (!blockId) {
        return
      }

      const htmlEl = element as HTMLElement
      const isAnnotated = annotatedBlockIds.has(blockId)
      const spanPosition = spanPositionMap.get(blockId)
      const tagColor = blockTagColors.get(blockId)

      element.classList.remove(
        TAGGED_BLOCK_CLASS,
        TAGGED_BLOCK_CLASS_LTR,
        TAGGED_BLOCK_CLASS_RTL,
        SPAN_START_CLASS,
        SPAN_MIDDLE_CLASS,
        SPAN_END_CLASS,
      )

      if (isAnnotated) {
        element.classList.add(TAGGED_BLOCK_CLASS, dirClass)

        if (spanPosition === 'start') {
          element.classList.add(SPAN_START_CLASS)
        } else if (spanPosition === 'middle') {
          element.classList.add(SPAN_MIDDLE_CLASS)
        } else if (spanPosition === 'end') {
          element.classList.add(SPAN_END_CLASS)
        }
      }

      if (tagColor) {
        htmlEl.style.setProperty('--tag-color', tagColor)
      } else if (htmlEl.style.getPropertyValue('--tag-color')) {
        htmlEl.style.removeProperty('--tag-color')
      }
    })
  }, [editor, isAuthor, direction, annotatedBlockIds, spanPositionMap, blockTagColors])

  // Inject styles on mount
  useEffect(() => {
    if (isAuthor) {
      injectStyles()
    }
  }, [isAuthor])

  // Refresh classes when annotations/AST change. The dependent useMemos rebuild on
  // the 150ms-debounced AST sync, which is the right cadence for class updates —
  // no per-keystroke registerUpdateListener needed.
  useEffect(() => {
    if (!isAuthor) {
      return
    }
    updateTaggedElements()
  }, [isAuthor, updateTaggedElements])

  // Create real side indicator elements for span annotations
  const sideIndicatorsRef = useRef<Map<string, HTMLDivElement>>(new Map())

  useEffect(() => {
    if (!isAuthor || !ast) {
      return
    }

    const rootElement = editor.getRootElement()
    const parentBox = rootElement?.parentElement
    if (!rootElement || !parentBox) {
      return
    }

    if (!parentBox.style.position || parentBox.style.position === 'static') {
      parentBox.style.position = 'relative'
    }

    const isRTL = direction === 'rtl'
    const existingIndicators = sideIndicatorsRef.current
    const newIndicatorIds = new Set<string>()

    for (const annotation of annotations) {
      if (isBlockSpanAnchor(annotation.anchor)) {
        const { startBlockId, endBlockId } = annotation.anchor
        const indicatorId = `span-indicator-${annotation.id}`
        newIndicatorIds.add(indicatorId)

        const firstBlockEl = rootElement.querySelector(`[data-block-id="${startBlockId}"]`) as HTMLElement
        const lastBlockEl = rootElement.querySelector(`[data-block-id="${endBlockId}"]`) as HTMLElement

        if (!firstBlockEl || !lastBlockEl) {
          continue
        }

        let indicator = existingIndicators.get(indicatorId)
        if (!indicator) {
          indicator = document.createElement('div')
          indicator.className = `lexical-span-side-indicator ${isRTL ? 'lexical-span-side-indicator-rtl' : 'lexical-span-side-indicator-ltr'}`
          indicator.dataset.annotationId = annotation.id
          parentBox.appendChild(indicator)
          existingIndicators.set(indicatorId, indicator)
        }

        const tagColor = findAnnotationColor(annotation, tagRegistry)
        if (tagColor) {
          indicator.style.setProperty('--tag-color', tagColor)
        } else {
          indicator.style.removeProperty('--tag-color')
        }

        const firstRect = firstBlockEl.getBoundingClientRect()
        const lastRect = lastBlockEl.getBoundingClientRect()
        const parentRect = parentBox.getBoundingClientRect()

        const top = firstRect.top - parentRect.top + parentBox.scrollTop
        const bottom = lastRect.bottom - parentRect.top + parentBox.scrollTop
        const height = Math.max(bottom - top, 20)

        indicator.style.top = `${top}px`
        indicator.style.height = `${height}px`

        if (isRTL) {
          const leftPos = firstRect.right - parentRect.left + 9
          indicator.style.left = `${leftPos}px`
          indicator.style.right = 'auto'
        } else {
          const leftPos = firstRect.left - parentRect.left - 12
          indicator.style.left = `${leftPos}px`
          indicator.style.right = 'auto'
        }
      }
    }

    for (const [id, indicator] of existingIndicators) {
      if (!newIndicatorIds.has(id)) {
        indicator.parentNode?.removeChild(indicator)
        existingIndicators.delete(id)
      }
    }

    return () => {
      for (const indicator of existingIndicators.values()) {
        indicator.parentNode?.removeChild(indicator)
      }
      existingIndicators.clear()
    }
  }, [editor, isAuthor, ast, annotations, direction, tagRegistry])

  // Build a map of blockId -> all blockIds in the same span (for span-wide hover)
  const spanBlocksMap = useMemo(() => {
    const map = new Map<string, string[]>()
    if (!ast) {
      return map
    }

    for (const annotation of annotations) {
      if (isBlockSpanAnchor(annotation.anchor)) {
        const { startBlockId, endBlockId } = annotation.anchor
        const startIndex = ast.blocks.findIndex((b) => b.id === startBlockId)
        const endIndex = ast.blocks.findIndex((b) => b.id === endBlockId)

        if (startIndex === -1 || endIndex === -1) {
          continue
        }

        const spanBlockIds: string[] = []
        for (let i = startIndex; i <= endIndex; i++) {
          spanBlockIds.push(ast.blocks[i].id)
        }

        for (const blockId of spanBlockIds) {
          map.set(blockId, spanBlockIds)
        }
      }
    }

    return map
  }, [annotations, ast])

  // Handle mouse hover for tag detection - use mouseover for faster response
  useEffect(() => {
    if (!isAuthor) {
      return
    }

    const rootElement = editor.getRootElement()
    if (!rootElement) {
      return
    }

    const parentBox = rootElement.parentElement
    const SPAN_HOVERED_CLASS = 'lexical-span-hovered'

    let overlayDiv: HTMLDivElement | null = null

    const removeOverlay = () => {
      if (overlayDiv && overlayDiv.parentNode) {
        overlayDiv.parentNode.removeChild(overlayDiv)
      }
      overlayDiv = null
    }

    const createOverlay = (firstEl: HTMLElement, lastEl: HTMLElement) => {
      removeOverlay()

      if (!parentBox) {
        return
      }

      overlayDiv = document.createElement('div')
      overlayDiv.className = 'lexical-span-overlay'

      const firstRect = firstEl.getBoundingClientRect()
      const lastRect = lastEl.getBoundingClientRect()
      const parentRect = parentBox.getBoundingClientRect()

      const top = firstRect.top - parentRect.top + parentBox.scrollTop - 4
      const bottom = lastRect.bottom - parentRect.top + parentBox.scrollTop + 4
      const left = Math.min(firstRect.left, lastRect.left) - parentRect.left - 4
      const right = Math.max(firstRect.right, lastRect.right) - parentRect.left + 4

      overlayDiv.style.top = `${top}px`
      overlayDiv.style.left = `${left}px`
      overlayDiv.style.width = `${right - left}px`
      overlayDiv.style.height = `${bottom - top}px`

      if (!parentBox.style.position || parentBox.style.position === 'static') {
        parentBox.style.position = 'relative'
      }
      parentBox.appendChild(overlayDiv)
    }

    const handleMouseOver = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      const blockElement = target.closest('[data-block-id]') as HTMLElement | null
      const blockId = blockElement?.getAttribute('data-block-id') || null

      if (blockId === hoveredBlockIdRef.current) {
        return
      }

      if (hoveredBlockIdRef.current) {
        const prevSpanBlocks = spanBlocksMap.get(hoveredBlockIdRef.current)
        if (prevSpanBlocks) {
          for (const id of prevSpanBlocks) {
            const el = rootElement.querySelector(`[data-block-id="${id}"]`)
            el?.classList.remove(SPAN_HOVERED_CLASS)
          }
        }
        parentBox?.querySelectorAll('.lexical-span-side-indicator.hovered').forEach((el) => {
          el.classList.remove('hovered')
        })
        removeOverlay()
      }

      hoveredBlockIdRef.current = blockId

      if (blockId) {
        const currentSpanBlocks = spanBlocksMap.get(blockId)
        if (currentSpanBlocks && currentSpanBlocks.length > 0) {
          for (const id of currentSpanBlocks) {
            const el = rootElement.querySelector(`[data-block-id="${id}"]`)
            el?.classList.add(SPAN_HOVERED_CLASS)
          }

          const sideIndicator = parentBox?.querySelector(
            `.lexical-span-side-indicator[data-annotation-id]`,
          ) as HTMLElement
          if (sideIndicator) {
            for (const annotation of annotations) {
              if (isBlockSpanAnchor(annotation.anchor)) {
                const spanBlocks = spanBlocksMap.get(annotation.anchor.startBlockId)
                if (spanBlocks?.includes(blockId)) {
                  const indicator = parentBox?.querySelector(`[data-annotation-id="${annotation.id}"]`)
                  indicator?.classList.add('hovered')
                  break
                }
              }
            }
          }

          const firstBlockEl = rootElement.querySelector(`[data-block-id="${currentSpanBlocks[0]}"]`) as HTMLElement
          const lastBlockEl = rootElement.querySelector(
            `[data-block-id="${currentSpanBlocks[currentSpanBlocks.length - 1]}"]`,
          ) as HTMLElement
          if (firstBlockEl && lastBlockEl) {
            createOverlay(firstBlockEl, lastBlockEl)
          }
        }
      }

      if (onBlockHover) {
        if (blockId && annotatedBlockIds.has(blockId) && blockElement && parentBox) {
          const isRTL = direction === 'rtl'

          const currentSpanBlocks = spanBlocksMap.get(blockId)
          let targetElement = blockElement
          let targetBlockId = blockId

          if (currentSpanBlocks && currentSpanBlocks.length > 0) {
            const firstBlockId = currentSpanBlocks[0]
            const firstBlockEl = rootElement.querySelector(`[data-block-id="${firstBlockId}"]`) as HTMLElement
            if (firstBlockEl) {
              targetElement = firstBlockEl
              targetBlockId = firstBlockId
            }
          }

          const rect = targetElement.getBoundingClientRect()
          const parentRect = parentBox.getBoundingClientRect()
          const topPosition = rect.top - parentRect.top + parentBox.scrollTop

          onBlockHover(targetBlockId, {
            top: topPosition,
            left: isRTL ? null : 8,
            right: isRTL ? 8 : null,
          })
        } else {
          onBlockHover(null, null)
        }
      }
    }

    const handleMouseLeave = () => {
      if (hoveredBlockIdRef.current) {
        const prevSpanBlocks = spanBlocksMap.get(hoveredBlockIdRef.current)
        if (prevSpanBlocks) {
          for (const id of prevSpanBlocks) {
            const el = rootElement.querySelector(`[data-block-id="${id}"]`)
            el?.classList.remove(SPAN_HOVERED_CLASS)
          }
        }
        parentBox?.querySelectorAll('.lexical-span-side-indicator.hovered').forEach((el) => {
          el.classList.remove('hovered')
        })
        hoveredBlockIdRef.current = null
        removeOverlay()
      }
      onBlockHover?.(null, null)
    }

    rootElement.addEventListener('mouseover', handleMouseOver)
    rootElement.addEventListener('mouseleave', handleMouseLeave)

    return () => {
      rootElement.removeEventListener('mouseover', handleMouseOver)
      rootElement.removeEventListener('mouseleave', handleMouseLeave)
      removeOverlay()
    }
  }, [editor, isAuthor, onBlockHover, direction, annotatedBlockIds, spanBlocksMap, annotations])

  // Cleanup: remove tagged classes when unmounting or when isAuthor becomes false
  useEffect(() => {
    return () => {
      const rootElement = editor.getRootElement()
      if (rootElement) {
        const taggedElements = rootElement.querySelectorAll(`.${TAGGED_BLOCK_CLASS}`)
        taggedElements.forEach((el) => {
          el.classList.remove(
            TAGGED_BLOCK_CLASS,
            TAGGED_BLOCK_CLASS_LTR,
            TAGGED_BLOCK_CLASS_RTL,
            SPAN_START_CLASS,
            SPAN_MIDDLE_CLASS,
            SPAN_END_CLASS,
          )
          ;(el as HTMLElement).style.removeProperty('--tag-color')
        })
      }
    }
  }, [editor])

  // This plugin doesn't render anything - it manages DOM classes and events
  return null
}
